import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Mail, Phone, MapPin, Globe, Linkedin, 
  Twitter, Instagram, Facebook, Loader2, Clock, Copy, Check
} from 'lucide-react';
import { toast } from 'sonner';
import AdSenseAd from '../components/AdSenseAd';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AutoWebsiteView = () => {
  const { websiteId } = useParams();
  const [website, setWebsite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchWebsite();
  }, [websiteId]);

  const fetchWebsite = async () => {
    try {
      const response = await axios.get(`${API}/auto-websites/${websiteId}`);
      setWebsite(response.data.website);
    } catch (error) {
      console.error('Error fetching website:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyWebsiteLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('Website link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!website) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Website Not Found</h1>
          <p className="text-gray-600">This website doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  const { userProfile, optionalInfo, templateData, selectedTemplate } = website;

  // Get hero background from selected template or fallback
  const heroBackground = selectedTemplate?.hero?.backgroundImage || 
    'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1600&q=80';
  
  const heroOverlay = selectedTemplate?.hero?.overlay || 
    'rgba(37, 99, 235, 0.85)';

  return (
    <div className="min-h-screen bg-white">
      {/* Copy Link Button - Fixed Position */}
      <button
        onClick={copyWebsiteLink}
        className="fixed top-4 right-4 z-50 bg-white hover:bg-gray-50 text-gray-900 px-4 py-2 rounded-full shadow-lg border border-gray-200 flex items-center gap-2 transition-all"
      >
        {copied ? (
          <>
            <Check className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-green-600">Copied!</span>
          </>
        ) : (
          <>
            <Copy className="w-4 h-4" />
            <span className="text-sm font-medium">Copy Link</span>
          </>
        )}
      </button>

      {/* Hero Section */}
      <section className="relative text-gray-900 py-20 px-4 bg-gradient-to-br from-blue-50 to-white">

        <div className="max-w-6xl mx-auto text-center relative z-10">
          {userProfile.profilePicUrl && (
            <img
              src={userProfile.profilePicUrl}
              alt={userProfile.name}
              className="w-32 h-32 rounded-full mx-auto mb-6 border-4 border-white shadow-xl object-cover"
            />
          )}
          <h1 className="text-5xl md:text-6xl font-bold mb-4">
            {templateData?.heroHeading || userProfile.businessName}
          </h1>
          {(templateData?.heroSubheading || userProfile.businessCategory) && (
            <p className="text-xl md:text-2xl mb-6 opacity-90">
              {templateData?.heroSubheading || userProfile.businessCategory}
            </p>
          )}
          {(optionalInfo?.tagline || userProfile.bio) && (
            <p className="text-lg max-w-3xl mx-auto opacity-90">
              {optionalInfo?.tagline || userProfile.bio}
            </p>
          )}
          
          {/* Contact Info */}
          <div className="flex flex-wrap gap-4 justify-center mt-8">
            {userProfile.email && (
              <a
                href={`mailto:${userProfile.email}`}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full transition shadow-lg"
              >
                <Mail className="w-4 h-4" />
                <span>{userProfile.email}</span>
              </a>
            )}
            {userProfile.phone && (
              <a
                href={`tel:${userProfile.phone}`}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-full transition shadow-lg"
              >
                <Phone className="w-4 h-4" />
                <span>{userProfile.phone}</span>
              </a>
            )}
          </div>
        </div>
      </section>

      {/* About Section */}
      {optionalInfo?.about && (
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
              {templateData?.aboutHeading || 'About Us'}
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed whitespace-pre-wrap">
              {optionalInfo.about}
            </p>
          </div>
        </section>
      )}

      {/* Ad - After About Section */}
      <section className="py-8 px-4 bg-gray-100">
        <div className="max-w-4xl mx-auto">
          <AdSenseAd format="horizontal" style={{ display: 'block', textAlign: 'center', minHeight: '90px' }} />
        </div>
      </section>

      {/* Services Section with Template Icons */}
      {optionalInfo?.services && optionalInfo.services.length > 0 && (
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              {templateData?.servicesHeading || 'Our Services'}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {optionalInfo.services.map((service, index) => (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition border border-gray-100"
                >
                  <div className="text-4xl mb-4">
                    {service.icon || selectedTemplate?.sections?.services?.defaultIcons?.[index] || '⭐'}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Ad - After Services Section */}
      <section className="py-8 px-4 bg-gray-100">
        <div className="max-w-4xl mx-auto">
          <AdSenseAd format="horizontal" style={{ display: 'block', textAlign: 'center', minHeight: '90px' }} />
        </div>
      </section>

      {/* Portfolio Section with Template Images */}
      {optionalInfo?.portfolio && optionalInfo.portfolio.length > 0 && (
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
              {templateData?.portfolioHeading || 'Our Work'}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {optionalInfo.portfolio.map((item, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition"
                >
                  <img
                    src={item.image || selectedTemplate?.sections?.portfolio?.defaultImages?.[index] || 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80'}
                    alt={item.title}
                    className="w-full h-48 object-cover"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80';
                    }}
                  />
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing Section */}
      {optionalInfo?.pricing && optionalInfo.pricing.length > 0 && (
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Pricing</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {optionalInfo.pricing.map((plan, index) => (
                <div
                  key={index}
                  className={`bg-white rounded-xl shadow-lg p-8 ${plan.popular ? 'border-4 border-blue-500 relative' : 'border border-gray-200'}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-bold">
                      Popular
                    </div>
                  )}
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="text-4xl font-bold text-blue-600 mb-6">{plan.price}</div>
                  <ul className="space-y-3 mb-8">
                    {plan.features?.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-green-500">✓</span>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Section */}
      {optionalInfo?.testimonials && optionalInfo.testimonials.length > 0 && (
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Testimonials</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {optionalInfo.testimonials.map((testimonial, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating || 5)].map((_, i) => (
                      <span key={i} className="text-yellow-400">⭐</span>
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                  <div className="border-t pt-4">
                    <p className="font-bold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      {optionalInfo?.faq && optionalInfo.faq.length > 0 && (
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {optionalInfo.faq.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Team Section */}
      {optionalInfo?.team && optionalInfo.team.length > 0 && (
        <section className="py-16 px-4 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Our Team</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {optionalInfo.team.map((member, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 text-center">
                  {member.photo && (
                    <img
                      src={member.photo}
                      alt={member.name}
                      className="w-32 h-32 rounded-full mx-auto mb-4 object-cover border-4 border-blue-100"
                    />
                  )}
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{member.name}</h3>
                  <p className="text-blue-600 font-medium mb-3">{member.role}</p>
                  <p className="text-gray-600 text-sm">{member.bio}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter Section */}
      {optionalInfo?.newsletter?.enabled && (
        <section className="py-16 px-4 bg-blue-600 text-white">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Stay Updated</h2>
            <p className="text-lg mb-8 opacity-90">
              {optionalInfo.newsletter.description || 'Subscribe to our newsletter for updates and special offers'}
            </p>
            <div className="flex gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-lg text-gray-900"
              />
              <button className="bg-white text-blue-600 px-6 py-3 rounded-lg font-bold hover:bg-blue-50 transition">
                Subscribe
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Ad - Bottom of Website */}
      <section className="py-8 px-4 bg-gray-100">
        <div className="max-w-4xl mx-auto">
          <AdSenseAd format="horizontal" style={{ display: 'block', textAlign: 'center', minHeight: '90px' }} />
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-12 text-center">
            {templateData?.contactHeading || 'Get In Touch'}
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h3 className="text-xl font-bold mb-4">Contact Information</h3>
              
              {userProfile.email && (
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 mt-1 text-blue-400" />
                  <div>
                    <p className="font-medium">Email</p>
                    <a 
                      href={`mailto:${userProfile.email}`}
                      className="text-blue-400 hover:text-blue-300"
                    >
                      {userProfile.email}
                    </a>
                  </div>
                </div>
              )}
              
              {userProfile.phone && (
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 mt-1 text-blue-400" />
                  <div>
                    <p className="font-medium">Phone</p>
                    <a 
                      href={`tel:${userProfile.phone}`}
                      className="text-blue-400 hover:text-blue-300"
                    >
                      {userProfile.phone}
                    </a>
                  </div>
                </div>
              )}
              
              {userProfile.address && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 mt-1 text-blue-400" />
                  <div>
                    <p className="font-medium">Address</p>
                    <p className="text-gray-300">{userProfile.address}</p>
                  </div>
                </div>
              )}
              
              {userProfile.website && (
                <div className="flex items-start gap-3">
                  <Globe className="w-5 h-5 mt-1 text-blue-400" />
                  <div>
                    <p className="font-medium">Website</p>
                    <a 
                      href={userProfile.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300"
                    >
                      {userProfile.website}
                    </a>
                  </div>
                </div>
              )}
            </div>

            <div>
              {optionalInfo?.hours && (
                <div className="mb-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Business Hours
                  </h3>
                  <p className="text-gray-300 whitespace-pre-wrap leading-relaxed">
                    {optionalInfo.hours}
                  </p>
                </div>
              )}

              {/* Social Media Links */}
              {(userProfile.linkedin || userProfile.twitter || userProfile.instagram || userProfile.facebook) && (
                <div>
                  <h3 className="text-xl font-bold mb-4">Follow Us</h3>
                  <div className="flex gap-4">
                    {userProfile.linkedin && (
                      <a
                        href={userProfile.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition"
                      >
                        <Linkedin className="w-6 h-6" />
                      </a>
                    )}
                    {userProfile.twitter && (
                      <a
                        href={userProfile.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition"
                      >
                        <Twitter className="w-6 h-6" />
                      </a>
                    )}
                    {userProfile.instagram && (
                      <a
                        href={userProfile.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition"
                      >
                        <Instagram className="w-6 h-6" />
                      </a>
                    )}
                    {userProfile.facebook && (
                      <a
                        href={userProfile.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition"
                      >
                        <Facebook className="w-6 h-6" />
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AutoWebsiteView;